import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useActiveRoom } from '../Hooks/useActiveRoom';
import { useSaltGenerator } from '../Hooks/useSaltGenerator';
import { ClosedRoomModal } from '../features/ClosedRoomModal/closed-room-modal';
import { useGameStatusStore } from '../game-status/store/game-status-store';

import { SignModal } from 'modules/ConnectPage/components/SignModal/SignModal';
import { Hash } from 'modules/Room/features/Hash/Hash';
import DesktopRoom from 'modules/Room/pages/DesktopRoom';
import { MobileRoom } from 'modules/Room/pages/mobile-room';

import { ChangeNetwork } from 'shared/components/sections/change-network/change-network';
import { ConnectWallet } from 'shared/components/sections/connect-wallet/connect-wallet';
import { RpcErrorModal } from 'shared/components/sections/rpc-error-modal/rpc-error-modal';
import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { BaseModal } from 'shared/components/ui/base-modal/base-modal';
import { ROUTES } from 'shared/constants';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { RoomStatusEnum, WinnerStatusEnum } from 'shared/constants/shared-enums';
import { useStartJoinAllowance } from 'shared/hooks/use-start-join-allowance';
import { useAccount, usePublicClient } from 'shared/hooks/use-web3';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import { useShowSignModal } from 'shared/hooks/useShowSignModal';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGamesStore } from 'shared/store/games-store';
import { useQuestsStore } from 'shared/store/questsStore';
import { useRoomStore } from 'shared/store/roomStore';
import { useStartGameStore } from 'shared/store/start-game-store';
import { useUIStore } from 'shared/store/uiStore';
import { getAllowance } from 'shared/viem-functions/start-game';

export const Room = () => {
  const [showClosedRoomModal, useShowClosedRoomModal] = useState(false);
  const [invitedRoomId, setInvitedRoomId] = useState<number | null>(null);

  const navigate = useNavigate();
  const { isConnected, chainId, address } = useAccount();

  const { activeRoomId, networkId } = useParams();
  const publicClient = usePublicClient();

  const { showSignModal } = useShowSignModal();
  const isMobile = useMediaQuery('(max-width: 1199px)');

  const {
    bet,
    playerA,
    playerB,
    resetStore,
    showHash,
    setShowHash,
    roomStatus,
    winnerStatus,
    amIPlayerA,
  } = useRoomStore();

  const { resetStore: resetGameStatusStore } = useGameStatusStore();
  let { startStore, activeRooms, clearStore } = useGamesStore();
  const { rpcErrorModal } = useUIStore();
  const { isJoining } = useStartGameStore();
  const { selectedCurrency } = useCurrenciesStore();
  const { fetchOpponentTopTiers, clearOpponentTopTiers } = useQuestsStore();

  useActiveRoom();
  useSaltGenerator();

  useEffect(() => {
    if (
      playerA.address !== NULL_ADDRESS &&
      playerA.address !== address?.toLowerCase() &&
      playerB.address !== NULL_ADDRESS &&
      playerB.address !== address?.toLowerCase()
    ) {
      console.log('watch game');
      navigate(`${ROUTES.STREAM}/${networkId}/${activeRoomId}`);
    }
  }, [playerA, playerB, address, activeRoomId, networkId]);

  useEffect(() => {
    return () => {
      resetStore();
      resetGameStatusStore();
    };
  }, []);

  useEffect(() => {
    if (
      winnerStatus === WinnerStatusEnum.PlayerAExitRoom &&
      roomStatus === RoomStatusEnum.Closed &&
      !amIPlayerA
    ) {
      useShowClosedRoomModal(true);
    }
  }, [winnerStatus, roomStatus]);

  useEffect(() => {
    if (roomStatus === RoomStatusEnum.Closed) {
      const opponentAddress =
        playerA.address === address?.toLowerCase() ? playerB.address : playerA.address;
      const roomToInvite = activeRooms.find(
        (room) =>
          room.data.playerB.toLowerCase() === address?.toLowerCase() &&
          room.data.playerA.toLowerCase() === opponentAddress.toLowerCase() &&
          room.status === RoomStatusEnum.Open,
      );

      if (roomToInvite) {
        setInvitedRoomId(Number(roomToInvite.data.roomId));
      }
    }
  }, [roomStatus, activeRooms]);

  useEffect(() => {
    if (publicClient && activeRoomId && roomStatus === RoomStatusEnum.Closed) {
      startStore(publicClient);
    } else if (activeRoomId && roomStatus !== RoomStatusEnum.Closed) {
      clearStore();
    }
  }, [publicClient, chainId, activeRoomId, roomStatus]);

  const { approveAllowanceHandler, startGameHandler, joinRoomHandler } = useStartJoinAllowance();

  const handleRevengeRequest = async () => {
    const playerToInvite = amIPlayerA ? playerB.address : playerA.address;
    const allowance = await getAllowance({
      ownerAddress: address as `0x${string}`,
      tokenAddress: selectedCurrency?.address as `0x${string}`,
      provider: publicClient,
    });
    let currentAllowance = Number(allowance) / 10 ** selectedCurrency?.decimals;
    if ((selectedCurrency.balance ?? 0) < +bet) {
      return;
    }
    // If we need to approve allowance, do that first
    if (Number(currentAllowance) < +bet) {
      await approveAllowanceHandler(false, +bet);
    }

    // Then start the game
    await startGameHandler(+bet, playerToInvite as `0x${string}`);
    resetStore();
    resetGameStatusStore();
  };

  useEffect(() => {
    if (playerB.address !== NULL_ADDRESS && playerA.address !== NULL_ADDRESS) {
      const opponentAddress = amIPlayerA ? playerB.address : playerA.address;

      fetchOpponentTopTiers(publicClient, opponentAddress);
    }

    return () => {
      clearOpponentTopTiers();
    };
  }, [amIPlayerA, playerB.address, playerA.address, fetchOpponentTopTiers]);

  const handleAcceptInvite = async (roomId: number) => {
    const allowance = await getAllowance({
      ownerAddress: address as `0x${string}`,
      tokenAddress: selectedCurrency?.address as `0x${string}`,
      provider: publicClient,
    });
    let currentAllowance = Number(allowance) / 10 ** selectedCurrency?.decimals;
    // First check if we have sufficient balance
    if ((selectedCurrency.balance ?? 0) < +bet) {
      return;
    }
    // If we need to approve allowance, do that first
    if (Number(currentAllowance) < +bet) {
      await approveAllowanceHandler(false, +bet);
    }
    // Then start the game
    await joinRoomHandler(roomId, +bet);
    resetStore();
    if (!isJoining) setInvitedRoomId(null);
  };

  if (!isConnected) {
    return <ConnectWallet title={'room.connect'} description={'room.connectDesc'} />;
  }

  if (Number(networkId) !== chainId) {
    return <ChangeNetwork title={'room.switch'} description={'room.switchDesc'} />;
  }
  return (
    <>
      <AnimatedTransition show={showSignModal}>
        <BaseModal>
          <SignModal />
        </BaseModal>
      </AnimatedTransition>
      <AnimatedTransition show={rpcErrorModal.show}>
        <BaseModal>
          <RpcErrorModal />
        </BaseModal>
      </AnimatedTransition>
      <AnimatedTransition show={showHash}>
        <BaseModal>
          <Hash setIsShow={setShowHash} />
        </BaseModal>
      </AnimatedTransition>
      <AnimatedTransition show={showClosedRoomModal}>
        <BaseModal>
          <ClosedRoomModal />
        </BaseModal>
      </AnimatedTransition>
      {isMobile ? (
        <MobileRoom
          key={activeRoomId}
          invitedRoomId={invitedRoomId}
          handleRevengeRequest={handleRevengeRequest}
          handleAcceptInvite={handleAcceptInvite}
        />
      ) : (
        <DesktopRoom
          key={activeRoomId}
          invitedRoomId={invitedRoomId}
          handleRevengeRequest={handleRevengeRequest}
          handleAcceptInvite={handleAcceptInvite}
        />
      )}
    </>
  );
};
